import React, { useState, useEffect } from 'react';
import { 
  ExclamationCircleIcon, 
  ExclamationTriangleIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { Link } from 'react-router-dom';
import { issueService, Issue } from '@/services/issueService';

interface KeyIssuesWidgetProps {
  tenantId: string;
  limit?: number;
}

export const KeyIssuesWidget: React.FC<KeyIssuesWidgetProps> = ({ 
  tenantId, 
  limit = 5 
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantId && user) {
      loadKeyIssues();
    }
  }, [tenantId, user]);

  const loadKeyIssues = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch active issues (OPEN, IN_PROGRESS) - sorted by urgency and impact
      const allIssues = await issueService.getIssues({
        status: undefined // Get all statuses, we'll filter client-side
      });
      
      // Filter for active issues (OPEN, IN_PROGRESS) and sort by priority
      const activeIssues = allIssues
        .filter((issue: Issue) => 
          issue.status === 'OPEN' || issue.status === 'IN_PROGRESS'
        )
        .sort((a: Issue, b: Issue) => {
          // Sort by urgency first (CRITICAL > HIGH > MEDIUM > LOW)
          const urgencyOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
          const urgencyDiff = (urgencyOrder[a.urgency] || 3) - (urgencyOrder[b.urgency] || 3);
          if (urgencyDiff !== 0) return urgencyDiff;
          
          // Then by impact (CRITICAL > HIGH > MEDIUM > LOW)
          const impactOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
          return (impactOrder[a.impact] || 3) - (impactOrder[b.impact] || 3);
        })
        .slice(0, limit);

      setIssues(activeIssues);
    } catch (error: any) {
      console.error('Error loading key issues:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to load key issues';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'CRITICAL':
        return 'text-red-600';
      case 'HIGH':
        return 'text-orange-600';
      case 'MEDIUM':
        return 'text-yellow-600';
      case 'LOW':
        return 'text-green-600';
      default:
        return 'text-[var(--color-secondary-text)]';
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'CRITICAL':
      case 'HIGH':
        return ExclamationTriangleIcon;
      default:
        return ExclamationCircleIcon;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RESOLVED':
        return 'text-green-600';
      case 'IN_PROGRESS':
        return 'text-blue-600';
      case 'OPEN':
        return 'text-yellow-600';
      case 'CLOSED':
        return 'text-gray-600';
      default:
        return 'text-[var(--color-secondary-text)]';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none">
        <CardHeader>
          <CardTitle className="text-[var(--color-surface-text)] flex items-center">
            <ExclamationCircleIcon className="h-5 w-5 mr-2" />
            {t('dashboard.keyIssues') || 'Key Issues'}
          </CardTitle>
          <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
            {t('dashboard.keyIssuesDescription') || 'Active issues requiring attention'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
            <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">{t('common.loading')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none">
        <CardHeader>
          <CardTitle className="text-[var(--color-surface-text)] flex items-center">
            <ExclamationCircleIcon className="h-5 w-5 mr-2" />
            {t('dashboard.keyIssues') || 'Key Issues'}
          </CardTitle>
          <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
            {t('dashboard.keyIssuesDescription') || 'Active issues requiring attention'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-sm text-red-600">{error}</p>
            <button 
              onClick={loadKeyIssues}
              className="mt-2 text-sm text-[var(--color-accent-text)] hover:underline"
            >
              {t('common.retry')}
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none">
      <CardHeader>
        <CardTitle className="text-[var(--color-surface-text)] flex items-center">
          <ExclamationCircleIcon className="h-5 w-5 mr-2" />
          {t('dashboard.keyIssues') || 'Key Issues'}
        </CardTitle>
        <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
          {t('dashboard.keyIssuesDescription') || 'Active issues requiring attention'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {issues.length === 0 ? (
          <div className="text-center py-4">
            <ExclamationCircleIcon className="h-12 w-12 opacity-50 mx-auto mb-2" />
            <p className="text-sm opacity-75">
              {t('dashboard.noKeyIssues') || 'No active issues'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {issues.map((issue) => {
              const UrgencyIcon = getUrgencyIcon(issue.urgency);
              return (
                <Link
                  key={issue.id}
                  to={`/issues/${issue.id}`}
                  className="group flex items-center justify-between p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] hover:bg-[var(--color-accent-bg)] hover:bg-opacity-10 hover:text-[var(--color-accent-text)] transition-colors cursor-pointer"
                >
                  <div className="flex-1 min-w-0 mr-2">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-sm font-medium truncate group-hover:text-[var(--color-accent-text)] transition-colors">
                        {issue.title}
                      </h4>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-surface-bg)] ${getUrgencyColor(issue.urgency)}`}>
                        <UrgencyIcon className="h-2.5 w-2.5 mr-0.5" />
                        {issue.urgency}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3 text-xs opacity-75">
                      <span className={getStatusColor(issue.status)}>
                        {issue.status}
                      </span>
                      {issue.created_at && (
                        <span className="flex items-center">
                          <CalendarIcon className="h-2.5 w-2.5 mr-0.5" />
                          {formatDate(issue.created_at)}
                        </span>
                      )}
                      {issue.assigned_to_name && (
                        <span className="flex items-center">
                          <UserIcon className="h-2.5 w-2.5 mr-0.5" />
                          {issue.assigned_to_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <EyeIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-50 group-hover:opacity-100" />
                </Link>
              );
            })}
          </div>
        )}

        {/* View All Issues Link */}
        <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
          <Link
            to="/issues"
            className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] hover:bg-opacity-90 rounded-md transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1.5" />
            {t('dashboard.viewAllIssues') || 'View All Issues'}
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

